import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Plus, Loader2, FileText } from "lucide-react";
import { uploadFile, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";

export function MultiImageUpload({ images = [], onChange, testid = "multi-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        uploaded.push(await uploadFile(f));
      }
      onChange([...(images || []), ...uploaded]);
      toast.success(`${uploaded.length} photo(s) uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const addUrl = () => {
    if (!url.trim()) return;
    onChange([...(images || []), url.trim()]);
    setUrl("");
  };

  const remove = (i) => onChange(images.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {(images || []).map((img, i) => (
          <div key={i} className="relative group aspect-[4/3] overflow-hidden rounded-sm border border-navy/10">
            <img src={img} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              data-testid={`${testid}-remove-${i}`}
              className="absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-navy-deep/80 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          data-testid={`${testid}-add`}
          className="flex aspect-[4/3] flex-col items-center justify-center gap-1 rounded-sm border-2 border-dashed border-navy/20 text-navy/50 hover:border-gold hover:text-gold transition-colors"
        >
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Plus className="h-5 w-5" />}
          <span className="text-xs">Upload</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
      <div className="mt-3 flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="…or paste an image URL" className="rounded-sm text-xs" />
        <button type="button" onClick={addUrl} className="shrink-0 rounded-sm border border-navy/15 px-4 text-sm text-navy hover:bg-navy hover:text-white transition-colors">Add</button>
      </div>
    </div>
  );
}

export function SingleImageUpload({ value, onChange, testid = "single-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file);
      onChange(url);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload →" className="rounded-sm" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="shrink-0 inline-flex items-center gap-2 rounded-sm border border-navy/15 px-4 py-2 text-sm text-navy hover:bg-navy hover:text-white transition-colors"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}

export function FileUpload({ value, onChange, accept = ".pdf", label = "PDF", testid = "file-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const url = await uploadFile(file);
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-2 rounded-sm bg-navy px-5 py-2.5 text-sm text-white hover:bg-gold hover:text-navy transition-colors"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload {label}
        </button>
        {value && (
          <a href={value} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-navy/70 hover:text-gold transition-colors">
            <FileText className="h-4 w-4" /> View current
          </a>
        )}
      </div>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  );
}
